import { Component } from '@theme/component';

/**
 * @typedef {Object} ReadMoreTextRefs
 * @property {HTMLElement} text - The clamped text element to measure
 * @property {HTMLButtonElement} button - The toggle button
 */

/**
 * A custom element that clamps text to a number of lines and reveals a
 * "Read more" toggle only when the content actually overflows. Clicking the
 * toggle opens the target sidebar (via `data-uc-sidebar-target`, handled by
 * sidebars.js) and activates its first tab.
 *
 * @extends {Component<ReadMoreTextRefs>}
 */
class ReadMoreText extends Component {
  requiredRefs = ['text', 'button'];

  /**
   * @type {ResizeObserver | undefined}
   */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#updateButtonVisibility());
      this.#resizeObserver.observe(this);
    }

    this.#updateButtonVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Shows the toggle button only when the clamped text overflows.
   */
  #updateButtonVisibility = () => {
    const { text, button } = this.refs;
    const overflows = text.scrollHeight > text.clientHeight + 1;

    button.hidden = !overflows;
  };

  /**
   * Opens the target sidebar and activates its first tab. Sidebar opening
   * itself is handled by sidebars.js via the button's `data-uc-sidebar-target`.
   */
  openSidebar = () => {
    const selector = this.refs.button.getAttribute('data-uc-sidebar-target');
    if (!selector) return;

    const sidebar = document.querySelector(selector);
    const firstTab = sidebar?.querySelector('[data-tab-button]');

    if (firstTab instanceof HTMLElement) firstTab.click();
  };
}

if (!customElements.get('read-more-text')) {
  customElements.define('read-more-text', ReadMoreText);
}

export default ReadMoreText;
